import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { Clock, Instagram, LifeBuoy, Mail, MapPin, Phone } from 'lucide-react'
import { Logo } from '@/components/generals/logo'
import { BookingButton } from '@/components/generals/button-link'
import { footerNav } from '@/constants/navigation'
import { siteConfig } from '@/constants/site'

const linkClasses = 'rounded-sm text-white/85 underline-offset-4 transition-colors hover:text-white hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-honey'

export const SiteFooter: FC = (): ReactNode => {
    const { contact } = siteConfig

    return (
        <footer className='bg-plum text-white'>
            <div className='mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-20'>
                <div className='grid gap-12 md:grid-cols-2 lg:grid-cols-12'>
                    <div className='lg:col-span-4'>
                        <Logo tone='light' />

                        <p className='mt-5 max-w-sm leading-relaxed text-white/85'>
                            Anxiety and burnout counseling in Olympia and online across Washington.
                        </p>

                        <BookingButton variant='honey' className='mt-7' />
                    </div>

                    <nav aria-label='Footer' className='lg:col-span-2 lg:col-start-6'>
                        <h2 className='text-sm font-semibold uppercase tracking-[0.18em] text-white'>
                            Explore
                        </h2>

                        <ul className='mt-5 space-y-3'>
                            {footerNav.map((item) => (
                                <li key={item.href}>
                                    <Link href={item.href} className={linkClasses}>
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className='lg:col-span-3'>
                        <h2 className='text-sm font-semibold uppercase tracking-[0.18em] text-white'>
                            Get in touch
                        </h2>

                        <ul className='mt-5 space-y-4'>
                            <li className='flex gap-3'>
                                <Mail className='mt-0.5 size-5 shrink-0 text-honey' aria-hidden='true' />
                                <a href={`mailto:${contact.email}`} className={`${linkClasses} break-all`}>
                                    {contact.email}
                                </a>
                            </li>

                            <li className='flex gap-3'>
                                <Phone className='mt-0.5 size-5 shrink-0 text-honey' aria-hidden='true' />
                                <a href={contact.phoneHref} className={linkClasses}>
                                    {contact.phone}
                                </a>
                            </li>

                            <li className='flex gap-3 text-white/85'>
                                <Clock className='mt-0.5 size-5 shrink-0 text-honey' aria-hidden='true' />
                                <span>
                                    {contact.hours}
                                </span>
                            </li>
                        </ul>
                    </div>

                    <div className='lg:col-span-3'>
                        <h2 className='text-sm font-semibold uppercase tracking-[0.18em] text-white'>
                            Where we meet
                        </h2>

                        <ul className='mt-5 space-y-4'>
                            <li className='flex gap-3 text-white/85'>
                                <MapPin className='mt-0.5 size-5 shrink-0 text-honey' aria-hidden='true' />
                                <span>
                                    {contact.location}
                                </span>
                            </li>

                            <li className='flex gap-3'>
                                <Instagram className='mt-0.5 size-5 shrink-0 text-honey' aria-hidden='true' />
                                <a href={contact.instagramUrl} target='_blank' rel='noopener noreferrer' className={`${linkClasses} break-all`}>
                                    {contact.instagramHandle}
                                    <span className='sr-only'> on Instagram (opens in a new tab)</span>
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className='border-t border-white/15'>
                <div className='mx-auto flex max-w-7xl flex-col gap-4 px-5 py-6 text-sm sm:px-8 md:flex-row md:items-center md:justify-between'>
                    <p className='flex flex-wrap items-center gap-x-2 gap-y-1 text-white/85'>
                        <span>Fernwell Counseling</span>
                        <span aria-hidden='true'>·</span>
                        <span>Olympia, Washington</span>
                        <span aria-hidden='true'>·</span>
                        <span>© 2026</span>
                        <span aria-hidden='true'>·</span>
                        <Link href='/privacy-policy' className={linkClasses}>
                            Privacy Policy
                        </Link>
                    </p>

                    <p className='inline-flex w-fit items-center gap-2 rounded-full bg-honey px-4 py-2 font-medium text-espresso'>
                        <LifeBuoy className='size-4 shrink-0' aria-hidden='true' />
                        <span>
                            In crisis? Call or text{' '}
                            <a href='tel:988' className='rounded-sm font-semibold underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-espresso'>
                                988
                            </a>
                            .
                        </span>
                    </p>
                </div>
            </div>
        </footer>
    )
}
